import { cva, type VariantProps } from 'class-variance-authority'
import { Check, Plus, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../lib/utils'

const chipVariants = cva(
  // The focus ring is the DS pelorous, matching Button / PillButton / Toggle / SegmentedToggle /
  // TopbarButton. It was `ring-2 ring-es-green-5 ring-offset-1`, which predates the focus pass —
  // it PASSED (3.67:1 on the cream page, over SC 1.4.11's 3:1) but was a different colour and a
  // thinner ring than every control beside it, so a chip focused differently from its neighbours.
  // NOT `transition-all`. Tailwind builds a ring out of TWO box-shadow layers — the ring itself and
  // an offset band painted on top of it — so transitioning box-shadow animates the focus indicator
  // in. Measured at 64ms into that animation: 1.18px of cream (#EAE5D7, the page background) over
  // 1.76px of visible pelorous, i.e. a white flash before the blue resolves. A focus ring has to be
  // instant. The hover lift's shadow snaps with it, which is imperceptible under the transform.
  'inline-flex items-center gap-2 rounded-full border-[1.5px] whitespace-nowrap transition-[transform,border-color,background-color,color] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  {
    variants: {
      shape: {
        // 40px, deliberately under the 44px floor Apple's HIG and WCAG 2.5.5 (AAA) both name — being
        // trialled for density and expected to be revisited. `min-h`, not `h`: the profiles list has
        // full-width chips that wrap to two lines on mobile, and a fixed height would clip them.
        chip: 'min-h-10 pl-3 pr-4 py-2 text-sm font-medium',
        toggle: 'min-h-10 pl-3.5 pr-5 py-2.5 text-sm font-semibold',
        static: 'px-3.5 py-1.5 text-sm font-semibold',
        // Compact, read-only label pill (meal meta: cook time, cuisine).
        meta: 'gap-1.5 px-3 py-1 text-xs font-medium'
      },
      tone: {
        surface: 'bg-card border-border text-foreground shadow-card',
        on: 'bg-brand-green-tint border-brand-green-hairline text-brand-green-on-tint',
        ghost: 'bg-transparent border-dashed border-border text-faint',
        static: 'bg-brand-green-tint border-brand-green-hairline text-brand-green-on-tint',
        // meta tones — selected via the public `tone` prop on variant="meta". `meta` is the
        // neutral default; the rest cover the common read-only label colors. Amber is
        // deliberately absent: the level-up badge lives in LevelUpsChip, not here.
        meta: 'bg-surface-hover border-border text-muted-foreground dark:bg-white/[0.04]',
        metaAccent: 'bg-brand-green-tint border-brand-green-hairline text-brand-green-on-tint',
        metaCritical: 'bg-destructive/10 border-destructive/20 text-destructive',
        // Read-only dashed/muted label — e.g. de-emphasized "less of" items.
        metaGhost: 'bg-transparent border-dashed border-muted-foreground/25 text-muted-foreground',
        // Fixed white glass over a photo, so text/border stay dark in both themes.
        // brand-green-900 is fixed across themes (no .dark override), unlike the
        // theme-flipping es-text which went light in dark mode.
        metaOnImage: 'bg-white/90 border-white/40 text-brand-green-900 backdrop-blur-sm',
        // Dark glass for secondary photo labels, so they sit back from the white ones. A literal
        // scrim colour: it darkens an image, not a themed surface.
        metaOnImageQuiet: 'bg-[rgba(16,23,18,.42)] border-white/20 text-white backdrop-blur-sm'
      },
      interactive: {
        true: 'cursor-pointer hover:-translate-y-0.5 hover:border-brand-green-border hover:shadow-card-hover motion-reduce:hover:translate-y-0',
        false: ''
      }
    },
    compoundVariants: [
      { tone: 'ghost', interactive: true, class: 'hover:text-muted-foreground hover:shadow-none' },
      // Toggle "avoid" chips warm to a slight cream on hover (matches the prototype). Add/selectable
      // chips deliberately keep a white fill on hover — only their border/lift change.
      { shape: 'toggle', tone: 'surface', interactive: true, class: 'hover:bg-surface-hover' }
    ],
    defaultVariants: { shape: 'chip', tone: 'surface', interactive: false }
  }
)

type ChipVariant = 'add' | 'selectable' | 'toggle' | 'static' | 'meta'
type ToggleState = 'avoid' | 'ok' | 'ghost'
/** Color tone for the read-only `meta` label. `neutral` is the default; no amber (see LevelUpsChip). */
type MetaTone = 'neutral' | 'accent' | 'critical' | 'ghost' | 'onImage' | 'onImageQuiet'

// The cva shape/tone/interactive dimensions are set internally from `variant`, never by callers,
// so they're omitted here; `tone` below is the caller-facing meta tone, not the internal cva one.
interface ChipProps extends Omit<VariantProps<typeof chipVariants>, 'shape' | 'tone' | 'interactive'> {
  /** `add`/`selectable` share visuals (Plus→Check); `toggle` flips avoid↔ok; `static` is a read-only review pill; `meta` is a compact read-only label. */
  variant: ChipVariant
  /** add/selectable: whether the chip is currently selected/added. OMIT it entirely for a chip
   *  that performs a one-way action (navigating, opening a flow) rather than toggling — leaving it
   *  out drops `aria-pressed`, which would otherwise announce that action as an unpressed toggle
   *  and imply a second press turns it back off. */
  active?: boolean
  /** toggle only: which side of the avoid/ok toggle this chip is on. */
  state?: ToggleState
  /** meta only: color tone. Defaults to a neutral surface. */
  tone?: MetaTone
  /** meta only: optional leading icon (e.g. a clock or sparkle). */
  icon?: ReactNode
  children: ReactNode
  onClick?: () => void
  className?: string
  'aria-label'?: string
}

/** meta tone prop → the cva tone key that paints it. */
const META_TONE: Record<MetaTone, 'meta' | 'metaAccent' | 'metaCritical' | 'metaGhost' | 'metaOnImage' | 'metaOnImageQuiet'> = {
  neutral: 'meta',
  accent: 'metaAccent',
  critical: 'metaCritical',
  ghost: 'metaGhost',
  onImage: 'metaOnImage',
  onImageQuiet: 'metaOnImageQuiet'
}

/** Small circular icon badge used by the add/selectable chips. */
function ChipBadge({ active }: { active?: boolean }) {
  return (
    <span
      className={cn(
        'flex size-4 flex-shrink-0 items-center justify-center rounded-full transition-colors',
        active ? 'bg-brand-green-fill text-brand-green-on-fill' : 'bg-brand-green-tint text-brand-green-on-tint'
      )}>
      {active ? <Check className="size-3" strokeWidth={2.5} /> : <Plus className="size-3" strokeWidth={2.5} />}
    </span>
  )
}

export function Chip({
  variant,
  active,
  state = 'avoid',
  tone = 'neutral',
  icon,
  children,
  onClick,
  className,
  'aria-label': ariaLabel
}: ChipProps) {
  if (variant === 'meta') {
    return (
      <span className={cn(chipVariants({ shape: 'meta', tone: META_TONE[tone], interactive: false }), className)}>
        {icon}
        {children}
      </span>
    )
  }

  if (variant === 'static') {
    return (
      <span className={cn(chipVariants({ shape: 'static', tone: 'static', interactive: false }), className)}>
        <Check className="size-3.5 flex-shrink-0 text-brand-green-ink" strokeWidth={2.5} />
        {children}
      </span>
    )
  }

  if (variant === 'toggle') {
    const toggleTone = state === 'ok' ? 'on' : state === 'ghost' ? 'ghost' : 'surface'
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={state === 'ok'}
        aria-label={ariaLabel}
        className={cn(chipVariants({ shape: 'toggle', tone: toggleTone, interactive: true }), className)}>
        {state === 'ok' ? (
          <Check className="size-4 flex-shrink-0 text-brand-green-ink" strokeWidth={2.5} />
        ) : (
          <X className={cn('size-4 flex-shrink-0 text-faint', state === 'ghost' && 'opacity-50')} />
        )}
        {children}
      </button>
    )
  }

  // add / selectable
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={ariaLabel}
      className={cn(chipVariants({ shape: 'chip', tone: active ? 'on' : 'surface', interactive: true }), className)}>
      <ChipBadge active={active} />
      {children}
    </button>
  )
}
